import { PDFDocument } from "pdf-lib";
import { fetchSepaTemplateBytes } from "./PaymentSepaTemplate";

export const normalizeText = (value) => (value || "").toString().trim();

const fillPdfFieldWithValue = (field, value) => {
  if (!value) return false;
  if (typeof field?.setText === "function") {
    field.setText(value);
    return true;
  }
  return false;
};

const setFieldsByNamePattern = (fields, patterns, value) => {
  if (!value) return;
  fields.forEach((field) => {
    const name = (field.getName?.() || "").toLowerCase();
    if (patterns.some((pattern) => name.includes(pattern))) {
      fillPdfFieldWithValue(field, value);
    }
  });
};

export const buildSepaMandateData = ({
  profileData,
  accountHolder,
  iban,
  bankName,
  mandateStreet,
  mandateZip,
  mandateCity,
}) => {
  const accountId = normalizeText(profileData.account_id || profileData.id);
  const profileFullName = normalizeText(
    `${profileData.first_name || ""} ${profileData.last_name || ""}`,
  );
  return {
    mandateReference: accountId ? `${accountId} - sophyapp` : "",
    accountHolder: normalizeText(accountHolder) || profileFullName,
    iban: normalizeText(iban).replace(/\s+/g, ""),
    bankName: normalizeText(bankName),
    clinicName: normalizeText(profileData.clinic_name),
    fullName: profileFullName,
    street: normalizeText(mandateStreet) || normalizeText(profileData.street),
    zip: normalizeText(mandateZip) || normalizeText(profileData.zip),
    country: normalizeText(profileData.country),
    email: normalizeText(profileData.email),
    city: normalizeText(mandateCity),
    date: new Date().toLocaleDateString("de-DE"),
  };
};

export const generateSepaMandatePdf = async ({
  signatureDataUrl,
  profileData,
  accountHolder,
  iban,
  bankName,
  mandateStreet,
  mandateZip,
  mandateCity,
  t,
}) => {
  if (!signatureDataUrl) {
    throw new Error(t("payment.sepaSignatureRequired"));
  }

  const templateBytes = await fetchSepaTemplateBytes();
  const pdfDoc = await PDFDocument.load(templateBytes);
  const sepaData = buildSepaMandateData({
    profileData,
    accountHolder,
    iban,
    bankName,
    mandateStreet,
    mandateZip,
    mandateCity,
  });
  const form = pdfDoc.getForm();
  const fields = form.getFields();

  setFieldsByNamePattern(
    fields,
    ["mandatsreferenz", "mandate_reference", "mandat_ref", "mandate reference"],
    sepaData.mandateReference,
  );
  setFieldsByNamePattern(fields, ["iban"], sepaData.iban);
  setFieldsByNamePattern(fields, ["bank", "creditor"], sepaData.bankName);
  setFieldsByNamePattern(
    fields,
    ["account", "holder", "kontoinhaber"],
    sepaData.accountHolder,
  );
  setFieldsByNamePattern(fields, ["name"], sepaData.fullName);
  setFieldsByNamePattern(
    fields,
    ["clinic", "firma", "company"],
    sepaData.clinicName,
  );
  setFieldsByNamePattern(
    fields,
    ["street", "strasse", "straße"],
    sepaData.street,
  );
  setFieldsByNamePattern(fields, ["zip", "plz", "postal"], sepaData.zip);
  setFieldsByNamePattern(fields, ["city", "ort"], sepaData.city);
  setFieldsByNamePattern(fields, ["country", "land"], sepaData.country);
  setFieldsByNamePattern(fields, ["mail", "email"], sepaData.email);
  setFieldsByNamePattern(fields, ["date", "datum"], sepaData.date);

  const pages = pdfDoc.getPages();
  const lastPage = pages[pages.length - 1];
  const signatureImage = await pdfDoc.embedPng(signatureDataUrl);
  const sigWidth = 160;
  const sigHeight = 50;
  lastPage.drawImage(signatureImage, {
    x: Math.max(40, lastPage.getWidth() - sigWidth - 80),
    y: 70,
    width: sigWidth,
    height: sigHeight,
  });
  lastPage.drawText(
    `${sepaData.city}${sepaData.city ? ", " : ""}${sepaData.date}`,
    {
      x: 40,
      y: 80,
      size: 10,
    },
  );

  try {
    form.flatten();
  } catch (error) {
    console.warn("SEPA form flattening skipped:", error);
  }

  const bytes = await pdfDoc.save();
  const blob = new Blob([bytes], { type: "application/pdf" });
  const fileName = `SEPA_Mandat_${Date.now()}.pdf`;

  return { blob, fileName, sepaData };
};
